import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/cn';
import { sdk } from '@/lib/graphql';
import { qk } from '@/lib/query-client';
import { formatRelative } from '@/lib/format';
import type { NotificationFieldsFragment } from '@/graphql/generated';
import { Button } from '@/components/ui/button';
import { CountBadge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/misc';
import { EmptyState, ErrorState } from '@/components/ui/states';
import { notificationIcon } from './notification-icon';

/** Refresco de la campana: 60 s es suficiente y no castiga al servidor. */
const POLL_MS = 60_000;

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: qk.unreadNotificationCount,
    queryFn: () => sdk.UnreadNotificationCount(),
    refetchInterval: POLL_MS,
    // Un fallo de red aquí no debe llenar la pantalla de errores: la campana
    // simplemente se queda con el último valor conocido.
    retry: 1,
  });
}

export function NotificationRow({
  notification,
  onSelect,
  className,
}: {
  notification: NotificationFieldsFragment;
  onSelect?: () => void;
  className?: string;
}) {
  const { Icon, tone } = notificationIcon(notification.kind);
  const unread = !notification.readAt;

  return (
    <div
      className={cn(
        'flex gap-3 rounded-md px-2 py-2 transition-colors duration-fast',
        unread ? 'bg-primary-subtle/40' : 'bg-transparent',
        onSelect && 'cursor-pointer hover:bg-surface-hover',
        className,
      )}
      onClick={onSelect}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={
        onSelect
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
    >
      <span className={cn('mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full', tone)}>
        <Icon className="size-3.5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fg">{notification.title}</p>
        {notification.body ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{notification.body}</p>
        ) : null}
        <p className="mt-1 text-2xs text-fg-subtle">{formatRelative(notification.createdAt)}</p>
      </div>
      {unread ? (
        <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" aria-label="Sin leer" />
      ) : null}
    </div>
  );
}

export function NotificationsMenu() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const countQuery = useUnreadNotificationCount();

  const listQuery = useQuery({
    queryKey: qk.notifications(false),
    queryFn: () => sdk.Notifications({ unreadOnly: false, page: { page: 1, pageSize: 12 } }),
    refetchInterval: POLL_MS,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => sdk.MarkNotificationRead({ id }),
    // Optimista: marcar como leído debe sentirse inmediato. Si el servidor
    // rechaza, se restaura el estado anterior.
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: qk.notifications(false) });
      const previousList = queryClient.getQueryData(qk.notifications(false));
      const previousCount = queryClient.getQueryData(qk.unreadNotificationCount);

      queryClient.setQueryData(qk.notifications(false), (old: unknown) => {
        const data = old as { notifications: NotificationFieldsFragment[] } | undefined;
        if (!data) return old;
        return {
          notifications: data.notifications.map((n) =>
            n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n,
          ),
        };
      });
      queryClient.setQueryData(qk.unreadNotificationCount, (old: unknown) => {
        const data = old as { unreadNotificationCount: number } | undefined;
        if (!data) return old;
        return { unreadNotificationCount: Math.max(0, data.unreadNotificationCount - 1) };
      });

      return { previousList, previousCount };
    },
    onError: (_error, _id, context) => {
      if (context?.previousList) queryClient.setQueryData(qk.notifications(false), context.previousList);
      if (context?.previousCount)
        queryClient.setQueryData(qk.unreadNotificationCount, context.previousCount);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.unreadNotificationCount });
    },
  });

  const markAll = useMutation({
    mutationFn: () => sdk.MarkAllNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notifications(false) });
      void queryClient.invalidateQueries({ queryKey: qk.unreadNotificationCount });
    },
  });

  const unread = countQuery.data?.unreadNotificationCount ?? 0;
  const notifications = listQuery.data?.notifications ?? [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread ? `Notificaciones, ${unread} sin leer` : 'Notificaciones'}
        >
          <Bell aria-hidden="true" />
          {unread > 0 ? (
            <CountBadge value={unread} className="absolute -right-0.5 -top-0.5" aria-label={`${unread} sin leer`} />
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <p className="text-sm font-semibold text-fg">Notificaciones</p>
          {unread > 0 ? (
            <Button variant="ghost" size="xs" onClick={() => markAll.mutate()} loading={markAll.isPending}>
              <CheckCheck aria-hidden="true" />
              Marcar todas
            </Button>
          ) : null}
        </div>

        <div className="max-h-96 overflow-y-auto p-1">
          {listQuery.isPending ? (
            <div className="flex flex-col gap-2 p-2" aria-hidden="true">
              {Array.from({ length: 4 }, (_, index) => (
                <div key={index} className="flex gap-3">
                  <Skeleton className="size-7 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : listQuery.isError ? (
            <ErrorState error={listQuery.error} onRetry={() => void listQuery.refetch()} className="py-8" />
          ) : notifications.length === 0 ? (
            <EmptyState
              icon={<Bell aria-hidden="true" />}
              title="No tienes notificaciones"
              description="Aquí llegarán las asignaciones, los avisos de SLA y los mensajes nuevos."
              className="py-8"
            />
          ) : (
            notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onSelect={() => {
                  if (!notification.readAt) markRead.mutate(notification.id);
                  if (notification.deepLink) void navigate(notification.deepLink);
                }}
              />
            ))
          )}
        </div>

        <div className="border-t border-border p-1">
          <Button variant="ghost" size="sm" className="w-full" asChild>
            <Link to="/notificaciones">Ver todas</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
